import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { EVAL_PHONE_PHOTOS_ROUTE, EVAL_POINT_PHONE_PHOTOS_ROUTE, PDLC_BOARD_PATH } from '@atlas/shared';
import { useEffect, lazy, Suspense } from 'react';
import { Layout } from './components/common/Layout';
import { RouteLoading } from './components/common/RouteLoading';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
const TwoFactorChallengePage = lazy(() =>
  import('./pages/TwoFactorChallengePage').then((m) => ({ default: m.TwoFactorChallengePage })),
);
const EnrollTwoFactorPage = lazy(() =>
  import('./pages/EnrollTwoFactorPage').then((m) => ({ default: m.EnrollTwoFactorPage })),
);
const Recover2FAPage = lazy(() =>
  import('./pages/Recover2FAPage').then((m) => ({ default: m.Recover2FAPage })),
);
const ChangePasswordPage = lazy(() =>
  import('./pages/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })),
);
const SelectDivisionPage = lazy(() =>
  import('./pages/SelectDivisionPage').then((m) => ({ default: m.SelectDivisionPage })),
);
const TeamsChatPage = lazy(() =>
  import('./pages/teamsChat/TeamsChatPage').then((m) => ({ default: m.TeamsChatPage })),
);
const Home = lazy(() =>
  import('./pages/Home').then((m) => ({ default: m.Home })),
);
const WhatsNewPage = lazy(() =>
  import('./pages/WhatsNewPage').then((m) => ({ default: m.WhatsNewPage })),
);
const ProductList = lazy(() =>
  import('./pages/ProductList').then((m) => ({ default: m.ProductList })),
);
const ProductDetail = lazy(() =>
  import('./pages/ProductDetail').then((m) => ({ default: m.ProductDetail })),
);
const ProductOverviewTab = lazy(() =>
  import('./pages/productDetail/tabs/Overview').then((m) => ({ default: m.Overview })),
);
const ProductEvaluationsTab = lazy(() =>
  import('./pages/productDetail/tabs/Evaluations').then((m) => ({ default: m.Evaluations })),
);
// The evaluation process (docs/specs/2026-10-13-evaluation-process-design.md):
// children of the Evaluations tab, so the shell keeps that tab selected.
const StartEvaluationPage = lazy(() =>
  import('./pages/productDetail/evaluationProcess/StartEvaluationPage').then((m) => ({
    default: m.StartEvaluationPage,
  })),
);
const EvaluationSessionPage = lazy(() =>
  import('./pages/productDetail/evaluationProcess/EvaluationSessionPage').then((m) => ({
    default: m.EvaluationSessionPage,
  })),
);
// The evaluation process's phone photos page (2026-10-14, D31): opened from a
// board QR code, so chrome-less and mobile-first, behind the normal login.
const PhonePhotosPage = lazy(() =>
  import('./pages/productDetail/evaluationProcess/PhonePhotosPage').then((m) => ({
    default: m.PhonePhotosPage,
  })),
);
// The same for one point's photos (2026-10-14): opened from the point dialog's "From phone" QR code.
const PointPhonePhotosPage = lazy(() =>
  import('./pages/productDetail/evaluationProcess/PointPhonePhotosPage').then((m) => ({
    default: m.PointPhonePhotosPage,
  })),
);
const ProductPdlcTab = lazy(() =>
  import('./pages/productDetail/tabs/Pdlc').then((m) => ({ default: m.Pdlc })),
);
const ProductMetricsTab = lazy(() =>
  import('./pages/productDetail/tabs/Metrics').then((m) => ({ default: m.Metrics })),
);
const PdlcTaskDetailPage = lazy(() =>
  import('./pages/pdlc/PdlcTaskDetailPage').then((m) => ({ default: m.PdlcTaskDetailPage })),
);
const ProductFixtureDnaTab = lazy(() =>
  import('./pages/productDetail/tabs/FixtureDna').then((m) => ({ default: m.FixtureDna })),
);
const ProductProposalTab = lazy(() =>
  import('./pages/productDetail/tabs/Proposal').then((m) => ({ default: m.Proposal })),
);
const ProductSpecsTab = lazy(() =>
  import('./pages/productDetail/tabs/Specs').then((m) => ({ default: m.Specs })),
);
const ProductFormsTab = lazy(() =>
  import('./pages/productDetail/tabs/Forms').then((m) => ({ default: m.Forms })),
);
const AssetsTab = lazy(() => import('./pages/productDetail/tabs/Assets'));
const SharePointTab = lazy(() => import('./pages/productDetail/tabs/SharePoint'));
const CompetitiveAnalysisTab = lazy(() => import('./pages/productDetail/tabs/CompetitiveAnalysis'));
const ProductsSpreadsheet = lazy(() =>
  import('./pages/ProductsSpreadsheet').then((m) => ({ default: m.ProductsSpreadsheet })),
);
const TaskFileOpen = lazy(() =>
  import('./pages/pdlc/TaskFileOpen').then((m) => ({ default: m.TaskFileOpen })),
);
const ReportsPage = lazy(() =>
  import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const PdlcBoardPage = lazy(() =>
  import('./pages/pdlcBoard/PdlcBoardPage').then((m) => ({ default: m.PdlcBoardPage })),
);
const NamingQueuePage = lazy(() =>
  import('./pages/marketing/NamingQueuePage').then((m) => ({ default: m.NamingQueuePage })),
);
const MarketingRequestsPage = lazy(() =>
  import('./pages/marketing/MarketingRequestsPage').then((m) => ({ default: m.MarketingRequestsPage })),
);
const SyncfxContentPage = lazy(() =>
  import('./pages/marketing/SyncfxContentPage').then((m) => ({ default: m.SyncfxContentPage })),
);
const MarketingRequestFileOpen = lazy(() =>
  import('./pages/pdlc/TaskFileOpen').then((m) => ({ default: m.MarketingRequestFileOpen })),
);
const BatteryInfoPage = lazy(() =>
  import('./pages/BatteryInfo').then((m) => ({ default: m.BatteryInfoPage })),
);
const FixtureComparisonPage = lazy(() =>
  import('./pages/fixtureComparison/FixtureComparisonPage').then((m) => ({ default: m.FixtureComparisonPage })),
);
const PricingCalculatorPage = lazy(() =>
  import('./pages/PricingCalculatorPage').then((m) => ({ default: m.PricingCalculatorPage })),
);
const SdcmCalculatorPage = lazy(() =>
  import('./pages/SdcmCalculatorPage').then((m) => ({ default: m.SdcmCalculatorPage })),
);
const L70CalculatorPage = lazy(() =>
  import('./pages/L70CalculatorPage').then((m) => ({ default: m.L70CalculatorPage })),
);
const AddUserPage = lazy(() =>
  import('./pages/AddUserPage').then((m) => ({ default: m.AddUserPage })),
);
const EditUserPage = lazy(() =>
  import('./pages/EditUserPage').then((m) => ({ default: m.EditUserPage })),
);
const ManageUsersPage = lazy(() =>
  import('./pages/ManageUsersPage').then((m) => ({ default: m.ManageUsersPage })),
);
const OAuthConsentPage = lazy(() =>
  import('./pages/OAuthConsentPage').then((m) => ({ default: m.OAuthConsentPage })),
);
const AddProductPage = lazy(() =>
  import('./pages/AddProductPage').then((m) => ({ default: m.AddProductPage })),
);
const CreateEvaluationPage = lazy(() =>
  import('./pages/CreateEvaluationPage').then((m) => ({ default: m.CreateEvaluationPage })),
);
const SubmissionList = lazy(() =>
  import('./pages/SubmissionList').then((m) => ({ default: m.SubmissionList })),
);
const SubmissionDetail = lazy(() =>
  import('./pages/SubmissionDetail').then((m) => ({ default: m.SubmissionDetail })),
);
const DocsPage = lazy(() =>
  import('./pages/DocsPage').then((m) => ({ default: m.DocsPage })),
);
const HelpPage = lazy(() =>
  import('./pages/help/HelpPage').then((m) => ({ default: m.HelpPage })),
);
const LuxTestsPage = lazy(() =>
  import('./pages/LuxTestsPage').then((m) => ({ default: m.LuxTestsPage })),
);
const FormApprovalsPage = lazy(() =>
  import('./pages/FormApprovalsPage').then((m) => ({ default: m.FormApprovalsPage })),
);
const SignatureRequestsPage = lazy(() =>
  import('./pages/SignatureRequestsPage').then((m) => ({ default: m.SignatureRequestsPage })),
);
const FormRequestsPage = lazy(() =>
  import('./pages/FormRequestsPage').then((m) => ({ default: m.FormRequestsPage })),
);
const BulletinsPage = lazy(() =>
  import('./pages/bulletins/BulletinsPage').then((m) => ({ default: m.BulletinsPage })),
);
const BulletinDetailPage = lazy(() =>
  import('./pages/bulletins/BulletinDetailPage').then((m) => ({ default: m.BulletinDetailPage })),
);
const ApiDocsPage = lazy(() =>
  import('./pages/ApiDocsPage').then((m) => ({ default: m.ApiDocsPage })),
);
const VersionPage = lazy(() =>
  import('./pages/VersionPage').then((m) => ({ default: m.VersionPage })),
);
import { NotFoundPage } from './pages/NotFoundPage';
const DemoPrimitivesPage = lazy(() =>
  import('./pages/_DemoPrimitives').then((m) => ({ default: m.DemoPrimitivesPage })),
);
import { AdminLayout } from './pages/AdminLayout';
const AdminApiKeysPage = lazy(() =>
  import('./pages/AdminApiKeysPage').then((m) => ({ default: m.AdminApiKeysPage })),
);
const AdminAssetTypesPage = lazy(() => import('./pages/AdminAssetTypesPage'));
const AdminSuppliersPage = lazy(() => import('./pages/AdminSuppliersPage'));
const AdminOfficesPage = lazy(() => import('./pages/AdminOfficesPage'));
const AdminLoginLogsPage = lazy(() => import('./pages/AdminLoginLogsPage'));
const AdminSpecModulesPage = lazy(() =>
  import('./pages/AdminSpecModulesPage').then((m) => ({ default: m.AdminSpecModulesPage })),
);
const AdminFixtureAliasesPage = lazy(() =>
  import('./pages/AdminFixtureAliasesPage').then((m) => ({ default: m.AdminFixtureAliasesPage })),
);
const DevOpsAdminPage = lazy(() =>
  import('./pages/admin/DevOpsAdminPage').then((m) => ({ default: m.DevOpsAdminPage })),
);
const AdminBackupsPage = lazy(() =>
  import('./pages/admin/AdminBackupsPage').then((m) => ({ default: m.AdminBackupsPage })),
);
const AdminDevDashboardPage = lazy(() =>
  import('./pages/admin/AdminDevDashboardPage').then((m) => ({ default: m.AdminDevDashboardPage })),
);
const FeatureVisibilityAdminPage = lazy(() =>
  import('./pages/admin/FeatureVisibilityAdminPage').then((m) => ({ default: m.FeatureVisibilityAdminPage })),
);
const AdminProductTypesPage = lazy(() => import('./pages/admin/AdminProductTypesPage'));
const AdminShipmentStagesPage = lazy(() => import('./pages/admin/AdminShipmentStagesPage'));
const AdminBarcodeItemPrefixesPage = lazy(() => import('./pages/admin/AdminBarcodeItemPrefixesPage'));
const AdminNotificationListsPage = lazy(() => import('./pages/admin/AdminNotificationListsPage'));
const AdminAssetReferencePage = lazy(() => import('./pages/admin/AdminAssetReferencePage'));
const AdminMetadataFieldsPage = lazy(() => import('./pages/admin/AdminMetadataFieldsPage'));
const AdminPhaseColorsPage = lazy(() => import('./pages/admin/AdminPhaseColorsPage'));
const AdminPricingConfigPage = lazy(() => import('./pages/admin/AdminPricingConfigPage'));
const PriceListPage = lazy(() => import('./pages/PriceList/PriceListPage'));
const AdminPriceListConfigPage = lazy(() => import('./pages/admin/AdminPriceListConfigPage'));
const AdminEvalFormsPage = lazy(() => import('./pages/admin/AdminEvalFormsPage'));
const SmartsheetAdminPage = lazy(() => import('./pages/admin/SmartsheetAdminPage'));
const AdminStandardsBotPage = lazy(() => import('./pages/admin/AdminStandardsBotPage'));
const AdminEntraGroupsPage = lazy(() => import('./pages/admin/AdminEntraGroupsPage'));
const AdminAsanaAccessPage = lazy(() => import('./pages/admin/AdminAsanaAccessPage'));
const AdminLandingPage = lazy(() => import('./pages/admin/AdminLandingPage'));

/**
 * Every page below is a lazy route: `React.lazy` gives each one its own chunk,
 * so a visitor downloads only the pages they open. Before 2026-09-02 a single
 * 7 MB entry chunk carried all ~100 pages, and the login page scored 42 on
 * PageSpeed because of it. Three modules stay eager on purpose: LoginPage (the
 * first thing every visitor renders), NotFoundPage, and the AdminLayout shell.
 *
 * Suspense boundaries live in two places: inside `Layout` around its children,
 * so the side menu stays put while a page chunk loads, and around the outer
 * `<Routes>` for the chrome-less pages (login flow, share, form request).
 */
/** Dev dashboard — gated by Feature Visibility (seeded to admin + dev). */
const DevDashboardPage = lazy(() =>
  import('./pages/devDashboard/DevDashboardPage').then((module) => ({
    default: module.DevDashboardPage,
  })),
);

const AdminHelpPage = lazy(() =>
  import('./pages/admin/AdminHelpPage').then((module) => ({ default: module.AdminHelpPage })),
);

// Served at /bulletins/manage, not under /admin — see the route for why. The
// module still lives under pages/admin/ alongside the pickers and composer it
// imports; moving ~12 files would churn the diff without moving the UI.
const ManageBulletinsPage = lazy(() =>
  import('./pages/admin/AdminBulletinsPage').then((module) => ({
    default: module.AdminBulletinsPage,
  })),
);

const FileTransferPage = lazy(() => import('./pages/FileTransferPage'));

const DemoEntityListPage = lazy(() =>
  import('./pages/_DemoEntityList').then((m) => ({ default: m.DemoEntityListPage })),
);
const Notifications = lazy(() =>
  import('./pages/Notifications').then((m) => ({ default: m.Notifications })),
);
const ReportBugPage = lazy(() =>
  import('./pages/ReportBugPage').then((m) => ({ default: m.ReportBugPage })),
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const AppsLauncherPage = lazy(() =>
  import('./pages/AppsLauncherPage').then((m) => ({ default: m.AppsLauncherPage })),
);
const BugIntakePage = lazy(() =>
  import('./pages/BugIntakePage').then((m) => ({ default: m.BugIntakePage })),
);
const TechWritingIntakePage = lazy(() =>
  import('./pages/TechWritingIntakePage').then((m) => ({ default: m.TechWritingIntakePage })),
);
const BugsListPage = lazy(() =>
  import('./pages/BugsListPage').then((m) => ({ default: m.BugsListPage })),
);
const BugsCalendarPage = lazy(() =>
  import('./pages/BugsCalendarPage').then((m) => ({ default: m.BugsCalendarPage })),
);
const BugDetailPage = lazy(() =>
  import('./pages/bugTracker/BugDetailPage').then((m) => ({ default: m.BugDetailPage })),
);
const TaskBoardPage = lazy(() =>
  import('./pages/bugTracker/TaskBoardPage').then((m) => ({ default: m.TaskBoardPage })),
);
const ProductBugsTab = lazy(() =>
  import('./pages/productDetail/tabs/Bugs').then((m) => ({ default: m.Bugs })),
);
const ProductCommentsTab = lazy(() =>
  import('./pages/productDetail/tabs/Comments').then((m) => ({ default: m.Comments })),
);
const ProductExternalCommentsTab = lazy(() =>
  import('./pages/productDetail/tabs/ExternalComments').then((m) => ({ default: m.ExternalComments })),
);
const ProductShipmentsTab = lazy(() =>
  import('./pages/productDetail/tabs/Shipments').then((m) => ({ default: m.Shipments })),
);
const ProductSageTab = lazy(() =>
  import('./pages/productDetail/tabs/Sage').then((m) => ({ default: m.Sage })),
);
const ProductBulletinsTab = lazy(() =>
  import('./pages/productDetail/tabs/Bulletins').then((m) => ({ default: m.Bulletins })),
);
const ProductMarketingTab = lazy(() =>
  import('./pages/productDetail/tabs/Marketing').then((m) => ({ default: m.Marketing })),
);
const SharePage = lazy(() => import('./pages/SharePage'));
const FormRequestPage = lazy(() => import('./pages/FormRequestPage'));
const SigningRequestPage = lazy(() => import('./pages/SigningRequestPage'));
const ApprovalPage = lazy(() => import('./pages/ApprovalPage'));
const ShipmentsPage = lazy(() => import('./pages/ShipmentsPage'));
const BarcodesPage = lazy(() => import('./pages/BarcodesPage'));
const CompetitorsPage = lazy(() => import('./pages/competitors/CompetitorsPage'));
const CompetitorDetailPage = lazy(() => import('./pages/competitors/CompetitorDetailPage'));
const ProductsMasterPage = lazy(() => import('./pages/competitors/ProductsMasterPage'));
const FixtureDetailPage = lazy(() => import('./pages/competitors/FixtureDetailPage'));
const ShipmentDetailPage = lazy(() => import('./pages/ShipmentDetailPage'));
const ScanStationPage = lazy(() => import('./pages/physicalAssets/ScanStationPage'));
const AssetListPage = lazy(() => import('./pages/physicalAssets/AssetListPage'));
const AssetDetailPage = lazy(() => import('./pages/physicalAssets/AssetDetailPage'));
const AssetDashboardPage = lazy(() => import('./pages/physicalAssets/AssetDashboardPage'));
import { AssetTableFixtures } from './dev/AssetTableFixtures';
import { useAuth } from './contexts/AuthContext';

/** Legacy URL helper: `/users/:userId` → `/admin/users/:userId`. */
function RedirectToAdminUser() {
  const { userId } = useParams();
  return <Navigate to={`/admin/users/${userId ?? ''}`} replace />;
}

/** Legacy URL helper: `/pdlc/:productId` → the product's PDLC tab. */
function RedirectToProductPdlc() {
  const { productId } = useParams();
  return <Navigate to={`/products/${productId ?? ''}/pdlc`} replace />;
}

export default function App() {
  const { logout } = useAuth();

  // Listen for unauthorized events from API
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);

  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
      {import.meta.env.DEV && (
        <Route path="/__dev/asset-table" element={<AssetTableFixtures />} />
      )}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/share/:token" element={<SharePage />} />
      {/* External NPSO fill-out (#691) — outside ProtectedRoute and Layout: the
          person following this link has no ATLAS account. */}
      <Route path="/form-request/:token" element={<FormRequestPage />} />
      {/* External e-signing (#837) — same arrangement: no account, no Layout.
          The embedded Documenso iframe needs a `frame-src` entry in
          docker/nginx.conf for this path or it is blocked silently. */}
      <Route path="/sign/:token" element={<SigningRequestPage />} />
      {/* Form sign-off (#814) — outside ProtectedRoute and Layout on purpose: the
          page runs its own Microsoft sign-in and shows only the form, no nav. */}
      <Route path="/approve/:approvalId" element={<ApprovalPage />} />
      <Route path="/2fa/challenge" element={<TwoFactorChallengePage />} />
      <Route path="/2fa/enroll" element={<EnrollTwoFactorPage />} />
      <Route path="/2fa/recover" element={<Recover2FAPage />} />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute allowPasswordChange skipDivisionGate>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/select-division"
        element={
          <ProtectedRoute skipDivisionGate>
            <SelectDivisionPage />
          </ProtectedRoute>
        }
      />
      {/* Chrome-less by design: this is the chat on its own monitor or pinned
          as its own tab, so an AppBar and side menu would only take room from
          it. `skipDivisionGate` because a chat window has no business demanding
          a division be picked first. */}
      <Route
        path="/teams-chat"
        element={
          <ProtectedRoute skipDivisionGate>
            <TeamsChatPage />
          </ProtectedRoute>
        }
      />
      {/* Evaluation process (D31): the phone photos page a board QR code opens.
          No Layout (a phone has no room for it); signed out, the login comes
          back here, as the path carries the product and stage. */}
      <Route
        path={EVAL_PHONE_PHOTOS_ROUTE}
        element={
          <ProtectedRoute>
            <PhonePhotosPage />
          </ProtectedRoute>
        }
      />
      {/* Evaluation process: one point's phone photos page (2026-10-14), the same way. */}
      <Route
        path={EVAL_POINT_PHONE_PHOTOS_ROUTE}
        element={
          <ProtectedRoute>
            <PointPhonePhotosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/docs"
        element={
          <ProtectedRoute>
            <Layout>
              <DocsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Ad-hoc file transfer. The counterpart to /share/:token above: this is
          where the link is made, that is where an outsider redeems it. */}
      <Route
        path="/file-transfer"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<RouteLoading />}>
                <FileTransferPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Help wiki — ATLAS-owned, DB-backed content. Readable by every
          authenticated user, hence no requiredRoles. Distinct from /docs, which
          proxies external Chauvet Standards markdown from GitHub. */}
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Layout>
              <HelpPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help/:articleId"
        element={
          <ProtectedRoute>
            <Layout>
              <HelpPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Composing lives beside the archive, NOT under /admin, even though it is
          gated to composers (admin, pm, marketing). It sat in the admin panel until 2026-07-23 and PMs
          never found it: a surface behind a door labelled "Admin" reads as
          off-limits to the very people meant to use it. `/admin/bulletins`
          redirects here so older links and bookmarks still land.

          Declared BEFORE `/bulletins/:bulletinId` so "manage" can never be read
          as a bulletin id. React Router ranks static segments above dynamic ones
          regardless, but the ordering makes that independent of its ranking. */}
      <Route
        path="/bulletins/manage"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<RouteLoading />}>
                <ManageBulletinsPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Bulletin archive — every edition that actually shipped. Readable by
          every authenticated user, hence no requiredRoles; composing one is the
          admin/pm route above. */}
      <Route
        path="/bulletins"
        element={
          <ProtectedRoute>
            <Layout>
              <BulletinsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bulletins/:bulletinId"
        element={
          <ProtectedRoute>
            <Layout>
              <BulletinDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lux-tests"
        element={
          <ProtectedRoute>
            <Layout>
              <LuxTestsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Outstanding NPSO fill-out requests (#739). Same roles as sending one;
          listed explicitly because ProtectedRoute widens 'admin' to pm only. */}
      {/* Every form sent for approval (#814). Same roles as sending one. */}
      <Route
        path="/form-approvals"
        element={
          <ProtectedRoute requiredRoles={['admin', 'pm', 'engineer']}>
            <Layout>
              <FormApprovalsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Every form sent for SIGNATURE (#837), the counterpart of /form-approvals.
          Same roles: the rows carry share tokens. */}
      <Route
        path="/signature-requests"
        element={
          <ProtectedRoute requiredRoles={['admin', 'pm', 'engineer']}>
            <Layout>
              <SignatureRequestsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/form-requests"
        element={
          <ProtectedRoute requiredRoles={['admin', 'pm', 'engineer']}>
            <Layout>
              <FormRequestsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-docs"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <Layout>
              <ApiDocsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/whats-new" element={<WhatsNewPage />} />
                <Route path="/version" element={<VersionPage />} />
                <Route
                  path="/_demo-primitives"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <DemoPrimitivesPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/products/new" element={<AddProductPage />} />
                <Route path="/products/:productId" element={<ProductDetail />}>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<ProductOverviewTab />} />
                  {/* Notes moved onto Overview; keep old links working. */}
                  <Route path="notes" element={<Navigate to="../overview" replace />} />
                  <Route path="evaluations" element={<ProductEvaluationsTab />} />
                  <Route path="evaluations/start" element={<StartEvaluationPage />} />
                  <Route path="evaluations/session/:sessionId" element={<EvaluationSessionPage />} />
                  <Route path="pdlc" element={<ProductPdlcTab />} />
                  <Route path="metrics" element={<ProductMetricsTab />} />
                  <Route path="fixture-dna" element={<ProductFixtureDnaTab />} />
                  <Route path="proposal" element={<ProductProposalTab />} />
                  <Route path="specs" element={<ProductSpecsTab />} />
                  <Route path="competitive-analysis" element={<CompetitiveAnalysisTab />} />
                  <Route path="forms" element={<ProductFormsTab />} />
                  <Route path="assets" element={<AssetsTab />} />
                  <Route path="sharepoint" element={<SharePointTab />} />
                  <Route path="bugs" element={<ProductBugsTab />} />
                  <Route path="comments" element={<ProductCommentsTab />} />
                  <Route path="external-comments" element={<ProductExternalCommentsTab />} />
                  <Route path="shipments" element={<ProductShipmentsTab />} />
                  <Route path="sage" element={<ProductSageTab />} />
                  <Route path="bulletins" element={<ProductBulletinsTab />} />
                  <Route path="marketing" element={<ProductMarketingTab />} />
                </Route>
                <Route
                  path="/products/:productId/pdlc/tasks/:taskGid"
                  element={<PdlcTaskDetailPage />}
                />
                {/* The link a task-file comment in Asana points at (#1108). */}
                <Route path="/files/:productId/:fileId" element={<TaskFileOpen />} />
                <Route path="/products" element={<ProductList />} />
                <Route
                  path="/products-spreadsheet"
                  element={
                    // Feature Visibility gates this one, not a role list —
                    // ProtectedRoute already refuses a page whose nav entry is
                    // hidden for the user, and the API gates on the same id.
                    <ProtectedRoute>
                      <ProductsSpreadsheet />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pdlc-board"
                  element={
                    // Feature Visibility gates this one, like the spreadsheet.
                    <ProtectedRoute>
                      <PdlcBoardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/pipeline"
                  element={
                    // The PDLC Board in its marketing view, behind its own switch.
                    <ProtectedRoute>
                      <PdlcBoardPage variant="marketing" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/naming"
                  element={
                    <ProtectedRoute>
                      <NamingQueuePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/requests"
                  element={
                    <ProtectedRoute>
                      <MarketingRequestsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/syncfx"
                  element={
                    <ProtectedRoute>
                      <SyncfxContentPage />
                    </ProtectedRoute>
                  }
                />
                {/* Like /files: no page gate, since the product card opens these too; the API checks access. */}
                <Route path="/marketing/requests/:id/files/:fileId" element={<MarketingRequestFileOpen />} />
                <Route
                  path="/battery-info"
                  element={
                    // Gated the same way as the products spreadsheet: Feature
                    // Visibility decides who reaches it, not a role list here.
                    <ProtectedRoute>
                      <BatteryInfoPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/price-lists"
                  element={
                    // No `requiredRoles`: Feature Visibility decides who reaches
                    // it, and the API gates on the same `nav:/price-lists` id.
                    <ProtectedRoute>
                      <PriceListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pricing-config"
                  element={
                    <ProtectedRoute>
                      <AdminPricingConfigPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/fixture-comparison" element={<FixtureComparisonPage />} />
                {/* Competitors — order matters: /products and /products/:id
                    must precede /:id so "products" is not captured as an id. */}
                <Route path="/competitors/products" element={<ProductsMasterPage />} />
                <Route path="/competitors/products/:productId" element={<FixtureDetailPage />} />
                <Route path="/competitors/:id" element={<CompetitorDetailPage />} />
                <Route path="/competitors" element={<CompetitorsPage />} />
                <Route
                  path="/tools/pricing/pro"
                  element={
                    <ProtectedRoute requiredRoles={['admin', 'pm']} requiredDivisions={['chauvet-professional']}>
                      <PricingCalculatorPage sheet="pro" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tools/pricing/dj"
                  element={
                    <ProtectedRoute requiredRoles={['admin', 'pm']} requiredDivisions={['chauvet-dj']}>
                      <PricingCalculatorPage sheet="dj" />
                    </ProtectedRoute>
                  }
                />
                <Route path="/tools/sdcm" element={<SdcmCalculatorPage />} />
                <Route path="/tools/l70" element={<L70CalculatorPage />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/apps" element={<AppsLauncherPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                {/* OAuth consent for an MCP connector. Inside the authenticated
                    app on purpose: an unauthenticated visitor goes through the
                    normal sign-in and comes back here. */}
                <Route path="/oauth/consent" element={<OAuthConsentPage />} />
                <Route path="/report-bug" element={<ReportBugPage />} />
                <Route path="/bugs/new" element={<BugIntakePage />} />
                <Route path="/bugs/board" element={<TaskBoardPage />} />
                <Route path="/bugs/calendar" element={<BugsCalendarPage />} />
                <Route path="/bugs/:gid" element={<BugDetailPage />} />
                <Route path="/bugs" element={<BugsListPage />} />
                <Route
                  path="/dev-dashboard"
                  element={
                    <ProtectedRoute>
                      <DevDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/tech-writing/new" element={<TechWritingIntakePage />} />
                <Route path="/tracking" element={<ShipmentsPage />} />
                <Route path="/barcodes" element={<BarcodesPage />} />
                {/* Physical Asset Tracking — order matters: /scan must precede
                    /:id so "scan" is not captured as an asset id. */}
                <Route path="/asset-tracking/scan" element={<ScanStationPage />} />
                <Route path="/asset-tracking/dashboard" element={<AssetDashboardPage />} />
                <Route path="/asset-tracking/:id" element={<AssetDetailPage />} />
                <Route path="/asset-tracking" element={<AssetListPage />} />
                <Route path="/tracking/:shipmentId" element={<ShipmentDetailPage />} />
                <Route path="/evaluations/new" element={<CreateEvaluationPage />} />
                <Route path="/evaluations" element={<SubmissionList />} />
                <Route path="/submissions/:submissionId" element={<SubmissionDetail />} />

                {/* Retired /pdlc pages; keep old Asana links and bookmarks landing. */}
                <Route path="/pdlc" element={<Navigate to={PDLC_BOARD_PATH} replace />} />
                <Route path="/pdlc/:productId" element={<RedirectToProductPdlc />} />
                {/* Admin shell — consolidates Users / People / API Keys under tabs */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminLandingPage />} />
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <ManageUsersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users/new"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AddUserPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users/:userId"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <EditUserPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="api-keys"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminApiKeysPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="asset-types"
                    element={
                      <ProtectedRoute>
                        <AdminAssetTypesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="suppliers"
                    element={
                      <ProtectedRoute>
                        <AdminSuppliersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="factories"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/admin/suppliers" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="offices"
                    element={
                      <ProtectedRoute>
                        <AdminOfficesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="login-logs"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminLoginLogsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="spec-modules"
                    element={
                      <ProtectedRoute>
                        <AdminSpecModulesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="fixture-aliases"
                    element={
                      <ProtectedRoute>
                        <AdminFixtureAliasesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="product-types"
                    element={
                      <ProtectedRoute>
                        <AdminProductTypesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="shipment-stages"
                    element={
                      <ProtectedRoute>
                        <AdminShipmentStagesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="barcode-item-prefixes"
                    element={
                      <ProtectedRoute>
                        <AdminBarcodeItemPrefixesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="asset-lists"
                    element={
                      <ProtectedRoute>
                        <AdminAssetReferencePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="metadata-fields"
                    element={
                      <ProtectedRoute>
                        <AdminMetadataFieldsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="phase-colors"
                    element={
                      <ProtectedRoute>
                        <AdminPhaseColorsPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Notification Lists renders the same panels as the
                      bulletin composer's Lists/Suppressions tabs — one store,
                      two entry points. Same admin+pm gate as those routes. */}
                  <Route
                    path="notification-lists"
                    element={
                      <ProtectedRoute>
                        <AdminNotificationListsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="eval-forms"
                    element={
                      <ProtectedRoute>
                        <AdminEvalFormsPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Help wiki authoring. admin and pm are equivalent here —
                      PMs publish directly, with no approval step. */}
                  <Route
                    path="help"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<RouteLoading />}>
                          <AdminHelpPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/* Moved out of the admin panel 2026-07-23 — kept as a
                      redirect, not deleted, because help articles and people's
                      bookmarks point here. `replace` so Back doesn't bounce
                      between the two paths. */}
                  <Route
                    path="bulletins"
                    element={<Navigate to="/bulletins/manage" replace />}
                  />
                  {/* Moved to the main sidebar 2026-08-26 — kept as redirects,
                      not deleted, because help articles and bookmarks point at
                      the old admin paths. Same treatment bulletins got above. */}
                  <Route path="price-lists" element={<Navigate to="/price-lists" replace />} />
                  <Route
                    path="pricing-config"
                    element={<Navigate to="/pricing-config" replace />}
                  />
                  <Route
                    path="price-list-config"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminPriceListConfigPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="smartsheet"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <SmartsheetAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="entra-groups"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminEntraGroupsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="asana-access"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminAsanaAccessPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="standards-bot"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminStandardsBotPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="backups"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminBackupsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="dev-dashboard"
                    element={
                      <ProtectedRoute>
                        <AdminDevDashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="devops"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <DevOpsAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="feature-visibility"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <FeatureVisibilityAdminPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Legacy redirects — keep old bookmarks working. */}
                <Route path="/users" element={<Navigate to="/admin/users" replace />} />
                <Route path="/users/new" element={<Navigate to="/admin/users/new" replace />} />
                <Route
                  path="/users/:userId"
                  element={<RedirectToAdminUser />}
                />
                <Route path="/people" element={<Navigate to="/admin/users" replace />} />
                <Route path="/admin/people" element={<Navigate to="/admin/users" replace />} />

                <Route
                  path="/_demo-entity-list"
                  element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <DemoEntityListPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
      </Routes>
    </Suspense>
  );
}
